import KeywordFilter from "components/filters/Filters/KeywordFilter";
import ListTemplate from "components/ListTemplate";
import React from "react";
import { mapFilterValuesToInstruction, mapResponseToFilterValues } from "./helpers";
import SmallDetails from "components/SmallDetails/SmallDetails";
import { routes } from "utilities/routing/generatedRoutes";
import SubscriptionDiv from "Client/Subscription/SubscriptionDiv";
import { discountApplies, subscriptionHasEndDate } from "Client/Detail/SubscriptionUtils";
import Arrow from "Client/Subscription/Arrow";
import moment from "moment";
import { ClientSortFilter } from "components/filters/Filters/SortFilter";
import ClientAccessFilter from "components/filters/Filters/ClientAccessFilter";
import { formatCurrency, formatShortDate } from "utilities";
import { findEnumObject } from "utilities/enumutils";
import DataClearedFilter from "components/filters/Filters/DataClearedFilter";
import HubSpotLinkedFilter from "components/filters/Filters/HubSpotLinkedFilter";
import { useLoaderData } from "react-router";
import PeriodFilter from "components/filters/Filters/PeriodFilter";
import CountryFilter from "components/filters/Filters/CountryFilter";

const DiscountDiv = ({ item }: { item: server.dto.ClientForAdminOneForList }) => {
  if (item.discount.percentage.value === 0) {
    return null;
  }

  const subLabel = item.discount?.until === null ?
    "perpetually" :
    `${RESX.DateTime.lblUntil} ${moment.utc(item.discount.until).format("ll")}`;

  return <SmallDetails
    label={`${item.discount.percentage.text} discount`}
    subLabel={subLabel}
  />
}

const ClientListApp = () => {
  const viewModel = useLoaderData<server.viewmodel.ClientListFrontModel>();

  return <ListTemplate
    title={RESX.Client.Clients}
    filterSet="client_ListAdminOne"
    defaultFilters={viewModel.defaultFilters}
    initialInstruction={viewModel.initialInstruction}
    fetchFunction={window.CoreApi.Client.Paginated}
    getId={item => item.clientId}
    layout={{
      type: "table",
      columns: [{
        key: "logo",
        header: "",
        render: item => <img width={50} height={50} src={item.iconUrl} />,
        size: "auto",
      }, {
        key: "title",
        header: RESX.Client.resxClient,
        render: item => {
          return <>
            <div>
              <a href={routes.client_DetailAdminOne(item.clientId).absolute} className="table-main-link">
                {item.name}
              </a> ({item.country.code})
            </div>

            <div className="s1_gray">
              {`client ${item.clientId}`}
            </div>

            {item.customerSince && <div className="s1_gray">
              since: {formatShortDate(moment.utc(item.customerSince))}
            </div>}

            {item.customerChurn && <div className="s1_gray">
              churn: {formatShortDate(moment.utc(item.customerChurn))}
            </div>}

            {item.dataCleared && <div className="s1_blue">data cleared</div>}
          </>
        },
        size: "1fr",
      }, {
        key: "hubspot",
        header: "",
        render: item => {
          return <div>
            {item.hubSpotDealId && <a href={`https://app.hubspot.com/contacts/5257648/record/0-3/${item.hubSpotDealId}/`} target="_blank" rel="noreferrer">
              HubSpot
            </a>}
          </div >
        }
      }, {
        key: "discount",
        header: "",
        render: item => {
          return <>
            <div>{formatCurrency(item.customerAnnualWorth, "eur", serverReference.culture.uiLocale, { hideCents: true })}</div>
            <div>
              <DiscountDiv item={item} />
            </div>
          </>
        }
      }, {
        key: "currentSubscription",
        header: RESX.Subscription.resxSubscription,
        render: item => {
          if (!item.activeSubscription) {
            return null;
          }

          return <SubscriptionDiv
            layout="small"
            start={item.activeSubscription.start}
            type={findEnumObject(item.activeSubscription.type, serverReference.subscriptionTypes)}
            cycle={findEnumObject(item.activeSubscription.billingCycle, serverReference.billingCycles)}
            users={item.activeSubscription.internalUsers}
            discount={item.activeSubscription.discount}
            onClick={() => { window.location.href = routes.client_DetailAdminOne(item.clientId).absolute }}
          />
        },
        size: "auto",
        style: { paddingRight: 0 }
      }, {
        key: "nextSubscriptionArrow",
        header: "",
        render: item => {
          if (!item.activeSubscription) {
            return null;
          }

          const hasEndDate = subscriptionHasEndDate(item.activeSubscription);
          if (!hasEndDate) {
            return null;
          }

          return <Arrow daysLeft={item.activeSubscription.daysUntilEnd} />
        },
        size: "auto",
        style: { paddingRight: 0 }
      }, {
        key: "nextSubscription",
        header: "",
        render: item => {
          if (!item.activeSubscription) {
            return null;
          }

          const hasEndDate = subscriptionHasEndDate(item.activeSubscription);
          const nextDiscountApplies = discountApplies(item.activeSubscription.end, item.discount?.until);
          const nextDiscountPercentage = (nextDiscountApplies) ? item.discount?.percentage : null;

          if (!hasEndDate) {
            return null;
          }

          return <SubscriptionDiv
            layout="small"
            start={item.activeSubscription.end}
            type={findEnumObject(item.nextSubscription.type, serverReference.subscriptionTypes)}
            cycle={findEnumObject(item.nextSubscription.billingCycle, serverReference.billingCycles)}
            users={item.nextSubscription.internalUsers}
            discount={nextDiscountPercentage}
            onClick={() => { window.location.href = routes.client_DetailAdminOne(item.clientId).absolute }}
          />
        }
      }]
    }}
    filters={<>
      <KeywordFilter placeholder="Search by name or ClientId" />
      <ClientAccessFilter initialClientAccesses={viewModel.clientAccesses} />
      <HubSpotLinkedFilter />
      <PeriodFilter targets={["customerSinceDate", "customerChurnDate"]} />
      <CountryFilter />
      <DataClearedFilter />
      <ClientSortFilter />
    </>}
    functions={{
      mapResponseToFilterValues,
      mapFilterValuesToInstruction
    }}
  />;
};

export default ClientListApp;